import { createSelector } from '@reduxjs/toolkit';
import { uniq } from 'es-toolkit';

import { defaultSavedItemsSlice, SavedItemStatus } from '@/redux/modules/item/saved/savedItems.types';
import { getItemSummaries } from '@/redux/modules/item/summary/itemSummary.selectors';
import { GlobalState } from '@/redux/store';

const stateSelector = (state: GlobalState) => state;
const savedItemsSlice = createSelector(stateSelector, (state) => state.savedItems);

const idSelector = (_: GlobalState, id: number) => id;

export const getSavedItemsMap = createSelector(savedItemsSlice, (state) => state?.savedItems);
const limitSelector = (_: GlobalState, limit: number) => limit;
const minSaveTsSelector = (_: GlobalState, _limit: number, minSaveTs = 0) => minSaveTs;

export const getSavedItem = createSelector([getSavedItemsMap, idSelector], (savedItems, id) => savedItems[id]);
export const getSavedItemsLoading = createSelector(savedItemsSlice, (state) => state.loading);
export const getSavedItems = createSelector(savedItemsSlice, (state) => state.ids);
export const getSavedItemsCount = createSelector(
    getSavedItemsMap,
    (savedItems) => Object.keys(savedItems).filter((item) => savedItems[item].status === SavedItemStatus.Saved).length
);
export const getSavedItemsPagination = createSelector(savedItemsSlice, (state) => ({
    ...defaultSavedItemsSlice.pagination,
    ...state.pagination,
}));

// return a unique list of catalog ids from the loaded saved items
export const getSavedItemCatalogIds = createSelector(
    (state: GlobalState) => {
        const itemIds = getSavedItems(state);
        return getItemSummaries(state, itemIds);
    },
    (items) => uniq(items.map((item) => item.catalogId))
);

export const getSavedItemsItemIds = createSelector(
    (state: GlobalState) => {
        const itemIds = getSavedItems(state);
        return getItemSummaries(state, itemIds);
    },
    (items) => uniq(items.map((item) => item.itemId))
);

export const getLimitedSavedItemIdsBySaveTime = createSelector(
    [getSavedItemsMap, limitSelector, minSaveTsSelector],
    (savedItems, limit, minSaveTs) =>
        Object.values(savedItems)
            .filter((item) => item.status === SavedItemStatus.Saved && item.saveTs >= minSaveTs)
            .sort((savedItem, nextSavedItem) => nextSavedItem.saveTs - savedItem.saveTs)
            .map((item) => item.itemId)
            .slice(0, limit)
);

export const isItemSaved = createSelector(getSavedItem, (savedItem) => savedItem?.status === SavedItemStatus.Saved);
